/**
 * The skill builder's native form primitives, shared by the multi-step CV
 * wizard (CreateSkillSheet) and the single-page portable-skill form
 * (PortableSkillForm): a labelled field, the rounded text input, and the icon
 * picker (preset glyph grid or an uploaded image).
 */
import { useState, type ReactNode } from 'react';
import {
  ActivityIndicator,
  Image,
  Modal,
  Pressable,
  StyleSheet,
  View,
} from 'react-native';
import { useTranslation } from 'react-i18next';
import { Text, TextInput } from '@/components/common/AppText';
import { Upload } from '@/components/icons/HugeIcons';
import { useTheme } from '../../hooks/useTheme';
import { useFont } from '../../providers/FontProvider';
import { useLocale } from '../../hooks/useLocale';
import { SkillIconGlyph } from './skill-builder-fields';
import { SKILL_ICON_OPTIONS, type SkillBuilderIcon } from './skill-builder-shared';

export function FieldLabel({ children }: { children: string }) {
  const { theme } = useTheme();
  const { fonts } = useFont();
  return (
    <Text style={[styles.fieldLabel, { color: theme.textSecondary, fontFamily: fonts.medium }]}>
      {children}
    </Text>
  );
}

export function Field({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: ReactNode;
}) {
  const { theme } = useTheme();
  const { fonts } = useFont();
  return (
    <View style={{ gap: 8 }}>
      <FieldLabel>{label}</FieldLabel>
      {children}
      {error ? (
        <Text style={[styles.errorText, { color: theme.danger, fontFamily: fonts.medium }]}>
          {error}
        </Text>
      ) : null}
    </View>
  );
}

export function WizardInput({
  value,
  onChangeText,
  placeholder,
  hasError,
  multiline,
  minHeight,
  maxLength,
  surface = 'secondary',
  fontSize = 15,
}: {
  value: string;
  onChangeText: (next: string) => void;
  placeholder: string;
  hasError?: boolean;
  multiline?: boolean;
  minHeight?: number;
  maxLength?: number;
  surface?: 'secondary' | 'background';
  fontSize?: number;
}) {
  const { theme } = useTheme();
  const { fonts } = useFont();
  const { isRTL } = useLocale();
  return (
    <TextInput
      value={value}
      onChangeText={onChangeText}
      placeholder={placeholder}
      placeholderTextColor={theme.textTertiary}
      multiline={multiline}
      maxLength={maxLength}
      style={[
        styles.input,
        {
          color: theme.text,
          backgroundColor:
            surface === 'background' ? theme.background : theme.surfaceSecondary,
          fontFamily: fonts.regular,
          fontSize,
          textAlign: isRTL ? 'right' : 'left',
        },
        multiline ? { minHeight: minHeight ?? 120, textAlignVertical: 'top' } : null,
        hasError ? { borderColor: theme.danger, borderWidth: 1 } : null,
      ]}
    />
  );
}

/** Square trigger that opens the preset-glyph grid, with an upload row. */
export function IconPickerButton({
  value,
  region,
  customUri,
  uploading,
  onChange,
  onPickImage,
}: {
  value: SkillBuilderIcon | 'flag';
  region: string;
  customUri: string | null;
  uploading?: boolean;
  onChange: (icon: SkillBuilderIcon) => void;
  onPickImage: () => void;
}) {
  const { t } = useTranslation('common');
  const { theme } = useTheme();
  const { fonts } = useFont();
  const [open, setOpen] = useState(false);

  return (
    <>
      <Pressable
        onPress={() => setOpen(true)}
        accessibilityRole="button"
        accessibilityLabel={t('skills.newFlow.basics.iconLabel')}
        style={[
          styles.trigger,
          { borderColor: theme.border, backgroundColor: theme.surfaceSecondary },
        ]}
      >
        {uploading ? (
          <ActivityIndicator size="small" color={theme.textTertiary} />
        ) : customUri ? (
          <Image source={{ uri: customUri }} style={styles.triggerImage} resizeMode="cover" />
        ) : (
          <SkillIconGlyph icon={value} region={region} size={24} />
        )}
      </Pressable>
      <Modal visible={open} transparent animationType="fade" onRequestClose={() => setOpen(false)}>
        <Pressable style={styles.scrim} onPress={() => setOpen(false)}>
          <Pressable
            style={[styles.card, { backgroundColor: theme.background }]}
            onPress={(e) => e.stopPropagation()}
          >
            <View style={styles.grid}>
              {SKILL_ICON_OPTIONS.map((icon) => {
                const active = icon === value && !customUri;
                return (
                  <Pressable
                    key={icon}
                    onPress={() => {
                      onChange(icon);
                      setOpen(false);
                    }}
                    accessibilityRole="button"
                    accessibilityState={{ selected: active }}
                    style={[
                      styles.cell,
                      {
                        borderWidth: active ? 1.5 : StyleSheet.hairlineWidth,
                        borderColor: active ? theme.text : theme.border,
                        backgroundColor: active ? theme.surfaceSecondary : 'transparent',
                      },
                    ]}
                  >
                    <SkillIconGlyph icon={icon} region={region} size={20} />
                  </Pressable>
                );
              })}
            </View>
            <Pressable
              onPress={() => {
                setOpen(false);
                onPickImage();
              }}
              accessibilityRole="button"
              style={styles.uploadRow}
            >
              <Upload size={18} color={theme.textSecondary} strokeWidth={1.9} />
              <Text style={[styles.uploadText, { color: theme.textSecondary, fontFamily: fonts.medium }]}>
                {t('skills.newFlow.basics.upload')}
              </Text>
            </Pressable>
          </Pressable>
        </Pressable>
      </Modal>
    </>
  );
}

const styles = StyleSheet.create({
  fieldLabel: { fontSize: 13, marginBottom: 8 },
  errorText: { fontSize: 13 },
  input: {
    minHeight: 52,
    borderRadius: 16,
    paddingHorizontal: 16,
    paddingVertical: 14,
  },
  trigger: {
    width: 56,
    height: 56,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: StyleSheet.hairlineWidth,
    overflow: 'hidden',
  },
  triggerImage: { width: '100%', height: '100%' },
  scrim: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.4)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
  },
  card: { width: 248, borderRadius: 20, padding: 16 },
  grid: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  cell: { width: 52, height: 52, borderRadius: 12, alignItems: 'center', justifyContent: 'center' },
  uploadRow: { flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 14, paddingVertical: 4 },
  uploadText: { fontSize: 14 },
});
